/**
 * Maston core technology.
 *
 * Desktop follows the pinned horizontal story gallery: vertical scroll is
 * scrubbed continuously across the 200vw track while the 100vh viewport is pinned.
 * Tablet/mobile use a dependency-free overflow scroller with CSS scroll snap.
 */

if (!customElements.get('maston-core-technology')) {
  const DESKTOP_QUERY = '(min-width: 1280px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const DEFAULT_AUTOPLAY_MS = 4000;
  const CROSS_FADE_MS = 350;
  const SNAP_DELAY_SECONDS = 0.08;
  const SNAP_DURATION_SECONDS = { min: 0.18, max: 0.48 };
  const SNAP_EDGE_THRESHOLD = 0.12;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class CorePanel {
    #root;
    #media;
    #track;
    #footerCaption;
    #previous;
    #next;
    #specs;
    #duration;
    #reducedMotion;
    #activeIndex = 0;
    #hoveredIndex = -1;
    #progressAnimation = null;
    #panelAbort = null;
    #carouselAbort = null;
    #scrollFrame = 0;
    #scrollAnimationFrame = 0;
    #isProgrammaticScroll = false;

    constructor(root, duration, reducedMotion) {
      this.#root = root;
      this.#media = root.querySelector('[data-core-media]');
      this.#track = root.querySelector('[data-core-track]');
      this.#footerCaption = root.querySelector('[data-core-caption-live]');
      this.#previous = root.querySelector('[data-core-previous]');
      this.#next = root.querySelector('[data-core-next]');
      this.#duration = duration;
      this.#reducedMotion = reducedMotion;
      this.#specs = [...root.querySelectorAll('[data-core-spec]')].map((spec) => {
        const figure = spec.querySelector('[data-core-figure]');
        return {
          root: spec,
          figure,
          figureParent: figure?.parentNode || null,
          figureNext: figure?.nextSibling || null,
          caption: spec.querySelector('[data-core-caption-source]'),
          card: spec.querySelector('[data-core-trigger]'),
          progress: spec.querySelector('[data-core-progress]')
        };
      });

      this.#panelAbort = new AbortController();
      const signal = this.#panelAbort.signal;
      this.#specs.forEach((spec, index) => {
        spec.card?.addEventListener('click', () => {
          if (index === this.#activeIndex) return;
          this.reveal(index, { restart: true });
        }, { signal });
        spec.card?.addEventListener('pointerenter', () => {
          this.#hoveredIndex = index;
          if (index === this.#activeIndex) this.pause();
        }, { signal });
        spec.card?.addEventListener('pointerleave', () => {
          if (this.#hoveredIndex === index) this.#hoveredIndex = -1;
          if (index === this.#activeIndex) this.resume();
        }, { signal });
      });
    }

    get root() {
      return this.#root;
    }

    get specCount() {
      return this.#specs.length;
    }

    destroy() {
      this.stop({ reset: true });
      this.destroyCarousel();
      this.restoreFigures();
      this.#panelAbort?.abort();
      this.#panelAbort = null;
    }

    setReducedMotion(reducedMotion) {
      this.#reducedMotion = reducedMotion;
      if (reducedMotion) this.stop({ reset: false });
    }

    prepareDesktop() {
      this.destroyCarousel();
      this.#specs.forEach((spec) => {
        if (spec.figure && this.#media && spec.figure.parentNode !== this.#media) {
          this.#media.append(spec.figure);
        }
      });
      this.reveal(0, { restart: false });
    }

    restoreFigures() {
      this.#specs.forEach((spec) => {
        if (!spec.figure || !spec.figureParent || spec.figure.parentNode === spec.figureParent) return;
        if (spec.figureNext?.parentNode === spec.figureParent) {
          spec.figureParent.insertBefore(spec.figure, spec.figureNext);
        } else {
          spec.figureParent.prepend(spec.figure);
        }
        spec.figure.removeAttribute('data-core-figure-active');
      });
    }

    activate({ reset = true, resume = false } = {}) {
      this.#root.inert = false;
      this.#root.removeAttribute('aria-hidden');
      if (reset) this.reveal(0, { restart: false });
      if (resume && this.resume()) return;
      this.play();
    }

    deactivate({ reset = true } = {}) {
      this.stop({ reset });
      this.#root.inert = true;
      this.#root.setAttribute('aria-hidden', 'true');
    }

    reveal(index, { restart = true } = {}) {
      if (!this.#specs.length) return;
      this.#activeIndex = clamp(index, 0, this.#specs.length - 1);

      this.#specs.forEach((spec, specIndex) => {
        const isActive = specIndex === this.#activeIndex;
        spec.root.dataset.coreSpecActive = String(isActive);
        spec.card?.setAttribute('aria-pressed', String(isActive));
        if (isActive) spec.figure?.setAttribute('data-core-figure-active', 'true');
        else spec.figure?.removeAttribute('data-core-figure-active');
        if (spec.progress) spec.progress.style.transform = 'scaleX(0)';
      });

      if (restart) {
        this.play();
        if (this.#hoveredIndex === this.#activeIndex) this.pause();
      }
    }

    revealSelectedElement(element, { carousel = false } = {}) {
      const specIndex = this.#specs.findIndex((spec) =>
        spec.root === element || spec.root.contains(element)
      );
      if (specIndex < 0) return;

      if (carousel) {
        this.#activeIndex = specIndex;
        this.#animateCarousel(this.#targetFor(specIndex));
        return;
      }

      this.reveal(specIndex, { restart: true });
    }

    play() {
      this.stop({ reset: false });
      if (this.#reducedMotion || this.#specs.length < 2 || this.#root.inert) return;

      const active = this.#specs[this.#activeIndex];
      if (!active?.progress) return;

      this.#progressAnimation = active.progress.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: this.#duration, easing: 'linear', fill: 'forwards' }
      );
      this.#progressAnimation.onfinish = () => {
        this.#progressAnimation = null;
        this.reveal((this.#activeIndex + 1) % this.#specs.length, { restart: true });
      };
    }

    pause() {
      if (this.#progressAnimation?.playState === 'running') this.#progressAnimation.pause();
    }

    resume() {
      if (this.#progressAnimation?.playState !== 'paused') return false;
      this.#progressAnimation.play();
      return true;
    }

    stop({ reset = false } = {}) {
      if (this.#progressAnimation) {
        this.#progressAnimation.onfinish = null;
        this.#progressAnimation.cancel();
        this.#progressAnimation = null;
      }
      if (reset) this.reveal(0, { restart: false });
    }

    initCarousel() {
      this.restoreFigures();
      this.stop({ reset: true });
      this.#root.inert = false;
      this.#root.removeAttribute('aria-hidden');
      if (!this.#track || this.#carouselAbort) return;

      this.#carouselAbort = new AbortController();
      const signal = this.#carouselAbort.signal;
      this.#track.addEventListener('scroll', this.#onCarouselScroll, { passive: true, signal });
      this.#previous?.addEventListener('click', () => this.#stepCarousel(-1), { signal });
      this.#next?.addEventListener('click', () => this.#stepCarousel(1), { signal });
      this.#syncCarouselState();
    }

    destroyCarousel() {
      this.#carouselAbort?.abort();
      this.#carouselAbort = null;
      if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
      if (this.#scrollAnimationFrame) cancelAnimationFrame(this.#scrollAnimationFrame);
      this.#scrollFrame = 0;
      this.#scrollAnimationFrame = 0;
      this.#isProgrammaticScroll = false;
      if (this.#track) {
        this.#track.style.removeProperty('scroll-snap-type');
        this.#track.style.removeProperty('pointer-events');
      }
    }

    #slides() {
      if (!this.#track) return [];
      return [...this.#track.children].filter((child) => child.matches('.shopify-block__maston-core-spec'));
    }

    #maxScroll() {
      return Math.max(0, (this.#track?.scrollWidth || 0) - (this.#track?.clientWidth || 0));
    }

    #targetFor(index) {
      const slides = this.#slides();
      if (!slides.length || !this.#track) return 0;
      const safeIndex = clamp(index, 0, slides.length - 1);
      if (safeIndex === 0) return 0;
      if (safeIndex === slides.length - 1) return this.#maxScroll();
      const trackRect = this.#track.getBoundingClientRect();
      const slideRect = slides[safeIndex].getBoundingClientRect();
      const centeredTarget = this.#track.scrollLeft
        + slideRect.left
        - trackRect.left
        - (this.#track.clientWidth - slideRect.width) / 2;
      return clamp(centeredTarget, 0, this.#maxScroll());
    }

    #stepCarousel(direction) {
      if (this.#isProgrammaticScroll) return;
      const slides = this.#slides();
      if (!slides.length) return;

      let targetIndex = clamp(this.#activeIndex + direction, 0, slides.length - 1);
      let target = this.#targetFor(targetIndex);
      if (direction < 0) {
        while (targetIndex > 0 && Math.abs(target - this.#track.scrollLeft) <= 1) {
          targetIndex -= 1;
          target = this.#targetFor(targetIndex);
        }
      }

      this.#activeIndex = targetIndex;
      this.#animateCarousel(target);
    }

    #animateCarousel(target) {
      if (!this.#track) return;
      if (this.#reducedMotion) {
        this.#track.scrollLeft = target;
        this.#syncCarouselState();
        return;
      }

      const start = this.#track.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) <= 1) return this.#syncCarouselState();

      const startedAt = performance.now();
      const duration = CROSS_FADE_MS;
      this.#isProgrammaticScroll = true;
      this.#track.style.scrollSnapType = 'none';
      this.#track.style.pointerEvents = 'none';

      const tick = (now) => {
        const progress = clamp((now - startedAt) / duration, 0, 1);
        const eased = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        this.#track.scrollLeft = start + distance * eased;
        if (progress < 1) {
          this.#scrollAnimationFrame = requestAnimationFrame(tick);
          return;
        }
        this.#scrollAnimationFrame = 0;
        this.#track.style.removeProperty('scroll-snap-type');
        this.#track.style.removeProperty('pointer-events');
        this.#isProgrammaticScroll = false;
        this.#syncCarouselState();
      };

      this.#scrollAnimationFrame = requestAnimationFrame(tick);
    }

    #onCarouselScroll = () => {
      if (this.#isProgrammaticScroll || this.#scrollFrame) return;
      this.#scrollFrame = requestAnimationFrame(() => {
        this.#scrollFrame = 0;
        this.#syncCarouselState();
      });
    };

    #syncCarouselState() {
      if (!this.#track) return;
      const slides = this.#slides();
      if (!slides.length) return;
      let nearest = 0;
      let nearestDistance = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(this.#targetFor(index) - this.#track.scrollLeft);
        if (distance < nearestDistance) {
          nearest = index;
          nearestDistance = distance;
        }
      });
      this.#activeIndex = nearest;
      if (this.#previous) this.#previous.disabled = this.#track.scrollLeft <= 1;
      if (this.#next) this.#next.disabled = this.#track.scrollLeft >= this.#maxScroll() - 1;
      this.#syncFooterCaption();
    }

    #syncFooterCaption() {
      if (!this.#footerCaption) return;
      const source = this.#specs[this.#activeIndex]?.caption;
      const value = source?.querySelector('.maston-core-technology__value')?.textContent || '';
      const labelSource = source?.querySelector('.maston-core-technology__label');
      const label = labelSource?.textContent || '';
      this.#footerCaption.replaceChildren();
      if (value) {
        const valueNode = document.createElement('p');
        valueNode.className = 'maston-core-technology__value';
        valueNode.textContent = value;
        this.#footerCaption.append(valueNode);
      }
      if (label) {
        const labelNode = document.createElement('p');
        labelNode.className = labelSource.className;
        labelNode.textContent = label;
        this.#footerCaption.append(labelNode);
      }
    }
  }

  class MastonCoreTechnology extends HTMLElement {
    #abort = null;
    #desktopQuery = window.matchMedia(DESKTOP_QUERY);
    #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    #panels = [];
    #pin = null;
    #panelsTrack = null;
    #tween = null;
    #scrollTrigger = null;
    #viewportObserver = null;
    #activePanelIndex = 0;
    #viewportVisible = false;
    #setupFrame = 0;
    #setupAttempts = 0;

    connectedCallback() {
      if (this.#abort) return;
      this.#abort = new AbortController();
      const signal = this.#abort.signal;
      const duration = Number.parseInt(this.dataset.coreAutoplay, 10) || DEFAULT_AUTOPLAY_MS;
      this.#pin = this.querySelector('[data-core-pin]');
      this.#panelsTrack = this.querySelector('[data-core-panels]');
      this.#panels = [...this.querySelectorAll('[data-core-panel]')]
        .map((panel) => new CorePanel(panel, duration, this.#reducedMotionQuery.matches));

      this.#desktopQuery.addEventListener('change', this.#syncMode, { signal });
      this.#reducedMotionQuery.addEventListener('change', this.#syncMode, { signal });
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
      this.#syncMode();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
      this.#teardownDesktop();
      this.#panels.forEach((panel) => panel.destroy());
      this.#panels = [];
    }

    #syncMode = () => {
      this.#panels.forEach((panel) => panel.setReducedMotion(this.#reducedMotionQuery.matches));
      if (this.#desktopQuery.matches && !this.#reducedMotionQuery.matches) {
        this.#waitForDesktopRuntime();
        return;
      }

      this.#teardownDesktop();
      this.dataset.coreMode = 'carousel';
      this.#panels.forEach((panel) => panel.initCarousel());
    };

    #waitForDesktopRuntime = () => {
      if (!this.isConnected || !this.#desktopQuery.matches || this.#reducedMotionQuery.matches) return;
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger || gsap?.core?.globals?.().ScrollTrigger;
      if (gsap && ScrollTrigger) {
        this.#setupAttempts = 0;
        this.#setupFrame = 0;
        this.#setupDesktop(gsap, ScrollTrigger);
        return;
      }

      this.#setupAttempts += 1;
      if (this.#setupAttempts > 180) {
        this.#setupFrame = 0;
        this.dataset.coreMode = 'static';
        return;
      }
      this.#setupFrame = requestAnimationFrame(this.#waitForDesktopRuntime);
    };

    #setupDesktop(gsap, ScrollTrigger) {
      if (this.#scrollTrigger || !this.#pin || !this.#panelsTrack || this.#panels.length < 2) return;
      this.#panels.forEach((panel) => panel.prepareDesktop());
      this.dataset.coreMode = 'pinned';
      this.style.setProperty('--mct-screen-count', String(this.#panels.length));
      this.#activePanelIndex = 0;
      this.#viewportVisible = false;
      this.#panels.forEach((panel) => panel.deactivate({ reset: true }));
      this.#observeViewport();

      gsap.registerPlugin(ScrollTrigger);
      const scrub = Number.parseFloat(this.dataset.coreScrub) || 1.2;
      const scrollFactor = Number.parseFloat(this.dataset.coreScrollFactor) || 1.4;

      this.#tween = gsap.fromTo(this.#panelsTrack, {
        x: 0
      }, {
        x: () => -this.#horizontalDistance(),
        ease: 'none',
        overwrite: true,
        scrollTrigger: {
          trigger: this.#pin,
          pin: true,
          pinSpacing: true,
          start: () => `top top+=${this.#viewportOffset()}`,
          end: () => `+=${Math.max(this.#horizontalDistance() * scrollFactor, this.#viewportHeight())}`,
          scrub,
          snap: {
            snapTo: (progress) => this.#edgeSnapProgress(progress),
            duration: SNAP_DURATION_SECONDS,
            delay: SNAP_DELAY_SECONDS,
            ease: 'power2.out',
            inertia: false
          },
          anticipatePin: 1,
          refreshPriority: 10,
          invalidateOnRefresh: true,
          onEnter: (instance) => this.#setPinActive(true, instance.progress),
          onEnterBack: (instance) => this.#setPinActive(true, instance.progress),
          onLeave: (instance) => this.#updateProgress(instance.progress),
          onLeaveBack: (instance) => this.#updateProgress(instance.progress),
          onUpdate: (instance) => this.#updateProgress(instance.progress),
          onRefresh: (instance) => this.#updateProgress(instance.progress)
        }
      });

      this.#scrollTrigger = this.#tween.scrollTrigger;
      window.requestScrollTriggerRefresh?.({ delay: 0 });
    }

    #observeViewport() {
      this.#viewportObserver?.disconnect();
      if (!this.#pin) return;

      this.#viewportObserver = new IntersectionObserver(([entry]) => {
        const visible = Boolean(entry?.isIntersecting && entry.intersectionRatio > 0);
        this.#setPinActive(visible, this.#scrollTrigger?.progress || 0);
      }, { threshold: 0 });
      this.#viewportObserver.observe(this.#pin);
    }

    #horizontalDistance() {
      return Math.max(0, (this.#panels.length - 1) * window.innerWidth);
    }

    #viewportHeight() {
      return Math.max(1, window.innerHeight - this.#viewportOffset());
    }

    #viewportOffset() {
      return Math.max(0, Number.parseFloat(this.dataset.coreViewportOffset) || 0);
    }

    #edgeSnapProgress(progress) {
      const last = Math.max(1, this.#panels.length - 1);
      const normalized = clamp(progress, 0, 1);
      const nearest = Math.round(normalized * last) / last;
      const edgeDistance = SNAP_EDGE_THRESHOLD / last;
      return Math.abs(normalized - nearest) <= edgeDistance ? nearest : normalized;
    }

    #setPinActive(active, progress = 0) {
      const wasVisible = this.#viewportVisible;
      this.#viewportVisible = active;
      if (!active) {
        this.#panels.forEach((panel, panelIndex) => {
          if (panelIndex === this.#activePanelIndex) panel.pause();
          else panel.deactivate({ reset: true });
        });
        return;
      }

      this.#updateProgress(progress, {
        forcePlayback: !wasVisible,
        resumePlayback: !wasVisible
      });
    }

    #updateProgress(progress, { forcePlayback = false, resumePlayback = false } = {}) {
      if (!this.#panels.length) return;
      const last = this.#panels.length - 1;
      const index = clamp(Math.round(clamp(progress, 0, 1) * last), 0, last);
      const changed = index !== this.#activePanelIndex;
      if (!changed && !forcePlayback) return;

      this.#panels.forEach((panel, panelIndex) => {
        if (panelIndex === index && this.#viewportVisible) {
          panel.activate({ reset: changed, resume: resumePlayback && !changed });
        } else {
          panel.deactivate({ reset: true });
        }
      });
      this.#activePanelIndex = index;
    }

    #goToPanel(index) {
      if (!this.#scrollTrigger || !this.#tween || this.#panels.length < 2) return false;
      const targetIndex = clamp(index, 0, this.#panels.length - 1);
      const progress = targetIndex / (this.#panels.length - 1);
      const start = this.#scrollTrigger.start;
      const end = this.#scrollTrigger.end;
      if (!Number.isFinite(start) || !Number.isFinite(end)) return false;

      this.#scrollTrigger.scroll(start + (end - start) * progress);
      this.#tween.progress(progress);
      this.#setPinActive(true, progress);
      return true;
    }

    #teardownDesktop() {
      if (this.#setupFrame) cancelAnimationFrame(this.#setupFrame);
      this.#setupFrame = 0;
      this.#setupAttempts = 0;
      const trigger = this.#scrollTrigger || this.#tween?.scrollTrigger;
      this.#viewportObserver?.disconnect();
      this.#viewportObserver = null;
      trigger?.kill(true);
      this.#tween?.kill();
      this.#scrollTrigger = null;
      this.#tween = null;
      this.#viewportVisible = false;
      this.style.removeProperty('--mct-screen-count');
      this.#panelsTrack?.style.removeProperty('transform');
      this.#panels.forEach((panel) => {
        panel.stop({ reset: true });
        panel.restoreFigures();
        panel.root.inert = false;
        panel.root.removeAttribute('aria-hidden');
      });
    }

    #onBlockSelect = (event) => {
      const selected = event.target;
      if (!(selected instanceof Element) || !this.contains(selected)) return;
      const panelRoot = selected.matches('[data-core-panel]')
        ? selected
        : selected.querySelector('[data-core-panel]') || selected.closest('[data-core-panel]');
      const panelIndex = this.#panels.findIndex((panel) => panel.root === panelRoot);
      if (panelIndex < 0) return;

      if (this.dataset.coreMode === 'pinned') this.#goToPanel(panelIndex);

      const selectedSpec = selected.matches('[data-core-spec]')
        ? selected
        : selected.querySelector('[data-core-spec]') || selected.closest('[data-core-spec]');
      if (selectedSpec) {
        this.#panels[panelIndex].revealSelectedElement(selectedSpec, {
          carousel: this.dataset.coreMode === 'carousel'
        });
      }
    };
  }

  customElements.define('maston-core-technology', MastonCoreTechnology);
}
